/**
 * Internationalization Configuration
 */

export const locales = ['en', 'nl', 'de', 'sv'] as const;
export type Locale = typeof locales[number];

export const defaultLocale: Locale = 'en';

export const localeNames: Record<Locale, string> = {
  en: 'English',
  nl: 'Nederlands',
  de: 'Deutsch',
  sv: 'Svenska',
};

export const localeFlags: Record<Locale, string> = {
  en: '🇬🇧',
  nl: '🇳🇱',
  de: '🇩🇪',
  sv: '🇸🇪',
};

/**
 * Hreflang values per locale.
 * Swedish uses `sv-SE` to target the Swedish market explicitly.
 */
export const localeHreflang: Record<Locale, string> = {
  en: 'en',
  nl: 'nl',
  de: 'de',
  sv: 'sv-SE',
};

/**
 * Open Graph locale identifiers per locale.
 */
export const localeOgLocale: Record<Locale, string> = {
  en: 'en_GB',
  nl: 'nl_NL',
  de: 'de_DE',
  sv: 'sv_SE',
};

/**
 * Full BCP-47 locale codes for schema.org `inLanguage` and the `<html lang>`
 * attribute. Use deze i.p.v. inline `locale === 'nl' ? 'nl-NL' : ...` ternaries.
 */
export const localeBcp47: Record<Locale, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  de: 'de-DE',
  sv: 'sv-SE',
};

/**
 * Get locale-specific path
 * English (default) doesn't get a prefix, other languages do
 */
export function getLocalePath(locale: Locale, path: string = ''): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  // English is at root, other languages get prefix
  if (locale === defaultLocale) {
    return cleanPath;
  }
  return `/${locale}${cleanPath === '/' ? '' : cleanPath}`;
}

/**
 * Check if locale is valid
 */
export function isValidLocale(locale: string): locale is Locale {
  return locales.includes(locale as Locale);
}
